import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Loader2, AlertTriangle } from 'lucide-react';
import { useProcurementAdjustments } from '@/hooks/useAdminProcurementAdjustment';
import { Procurement } from '@/types/database/procurement';
import { WarehouseAdjustmentForm, WarehouseFormData } from './WarehouseAdjustmentForm';
import { QualityAdjustmentForm, QualityFormData } from './QualityAdjustmentForm';
import { ItemAdjustmentSection } from './ItemAdjustmentSection';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface AdminAdjustmentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  procurement: Procurement;
}

export function AdminAdjustmentDialog({
  open,
  onOpenChange,
  procurement,
}: AdminAdjustmentDialogProps) {
  const [activeTab, setActiveTab] = useState('warehouse');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [reason, setReason] = useState('');
  
  // Warehouse form state
  const [warehouseData, setWarehouseData] = useState<WarehouseFormData>({
    received_quantity_kg: 0,
    compensation_kg: 0,
  });
  
  // Quality form state
  const [qualityData, setQualityData] = useState<QualityFormData>({
    damage_kg: 0,
    compensation_kg: 0,
  });

  const adjustmentMutation = useProcurementAdjustments();

  // Initialize form data from procurement
  useEffect(() => {
    if (procurement) {
      const warehouseReceived = (procurement.warehouse as any)?.received_quantity_kg ?? 0;
      const qualityCompensation = procurement.quality?.compensation_kg ?? 0;
      
      setWarehouseData({
        received_quantity_kg: warehouseReceived,
        compensation_kg: qualityCompensation,
      });
      
      setQualityData({
        damage_kg: procurement.quality?.damage_kg ?? 0,
        compensation_kg: qualityCompensation,
      });
    }
  }, [procurement]);

  const calculateNetPayable = () => {
    return Math.max(0, warehouseData.received_quantity_kg - warehouseData.compensation_kg);
  };

  const handleSave = () => {
    if (!reason.trim()) {
      return;
    }
    setShowConfirmation(true);
  };

  const handleConfirmSave = async () => {
    try {
      await adjustmentMutation.mutateAsync({
        procurementId: procurement.id,
        warehouseData: {
          received_quantity_kg: warehouseData.received_quantity_kg,
          net_kg_payable: calculateNetPayable(),
        },
        qualityData: {
          damage_kg: qualityData.damage_kg,
          compensation_kg: qualityData.compensation_kg,
        },
        reason: reason.trim(),
      });
      setShowConfirmation(false);
      onOpenChange(false);
    } catch (error) {
      // Error handled by mutation
    }
  };

  const hasMultipleItems = procurement.items && procurement.items.length > 1;
  const isReasonValid = reason.trim().length >= 10;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Adjust Procurement Data</DialogTitle>
            <DialogDescription>
              Make adjustments to warehouse received quantity and quality compensation before finance approval.
              All changes will be logged in the audit trail.
            </DialogDescription>
          </DialogHeader>

          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="warehouse">Warehouse Data</TabsTrigger>
              <TabsTrigger value="quality">Quality Data</TabsTrigger>
            </TabsList>

            <TabsContent value="warehouse" className="space-y-4 mt-4">
              <WarehouseAdjustmentForm
                data={warehouseData}
                onChange={setWarehouseData}
                netPayable={calculateNetPayable()}
              />
            </TabsContent>

            <TabsContent value="quality" className="space-y-4 mt-4">
              <QualityAdjustmentForm
                data={qualityData}
                onChange={(newData) => {
                  setQualityData(newData);
                  // Sync compensation with warehouse form
                  setWarehouseData(prev => ({
                    ...prev,
                    compensation_kg: newData.compensation_kg,
                  }));
                }}
              />
            </TabsContent>
          </Tabs>

          {hasMultipleItems && (
            <ItemAdjustmentSection
              items={procurement.items!}
              procurementId={procurement.id}
            />
          )}

          <div className="space-y-2 mt-4">
            <label className="text-sm font-medium">
              Adjustment Reason <span className="text-destructive">*</span>
            </label>
            <textarea
              className="w-full min-h-[80px] p-3 border rounded-md text-sm"
              placeholder="Explain why this adjustment is being made (minimum 10 characters)..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
            {reason.length > 0 && !isReasonValid && (
              <p className="text-xs text-destructive">
                Please provide a more detailed reason (at least 10 characters)
              </p>
            )}
          </div>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={!isReasonValid || adjustmentMutation.isPending}
            >
              {adjustmentMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={showConfirmation} onOpenChange={setShowConfirmation}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Confirm Adjustments
            </AlertDialogTitle>
            <AlertDialogDescription>
              You are about to modify procurement data. This action will:
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>Update warehouse received quantity to {warehouseData.received_quantity_kg} kg</li>
                <li>Update net payable to {calculateNetPayable()} kg</li>
                <li>Update quality compensation to {qualityData.compensation_kg} kg</li>
                <li>Log this change in the audit trail</li>
              </ul>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmSave}>
              Confirm Changes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
